import React, { useState, useEffect } from 'react';
import {
  PermissionBanner,
  PermissionContent,
  PermissionText,
  PermissionButton,
  CloseButton,
} from './styles';
import {
  getNotificationPermission,
  requestNotificationPermission,
  registerFCMToken,
} from '../../services/pushNotificationService';

interface NotificationPermissionProps {
  userId: number;
  onPermissionGranted?: () => void;
}

const NotificationPermission: React.FC<NotificationPermissionProps> = ({
  userId,
  onPermissionGranted,
}) => {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = () => {
    const currentPermission = getNotificationPermission();
    setPermission(currentPermission);
    
    // Se já tem permissão, não mostrar o banner
    if (currentPermission === 'granted') {
      setIsVisible(false);
    }
  };

  const handleRequestPermission = async () => {
    setIsLoading(true);
    try {
      console.log('🔔 Solicitando permissão de notificação...');
      const granted = await requestNotificationPermission();
      console.log('🔔 Resultado da permissão:', granted ? 'concedida' : 'negada');
      
      if (granted) {
        setPermission('granted');
        setIsVisible(false);
        
        // Registrar token FCM no backend (sem solicitar permissão novamente)
        console.log('🔔 Registrando token FCM...');
        const token = await registerFCMToken(userId, true); // skipPermissionCheck = true
        if (token) {
          console.log('✅ Permissão concedida e token registrado com sucesso');
          if (onPermissionGranted) {
            onPermissionGranted();
          }
        } else {
          console.warn('⚠️ Permissão concedida mas token não foi obtido');
        }
      } else {
        const currentPermission = getNotificationPermission();
        setPermission(currentPermission);
        if (currentPermission === 'denied') {
          alert('Permissão de notificação negada. Você pode habilitar nas configurações do navegador (ícone de cadeado ao lado da URL).');
        } else {
          alert('Permissão de notificação não foi concedida.');
        }
      }
    } catch (error) {
      console.error('❌ Erro ao solicitar permissão:', error);
      alert('Erro ao solicitar permissão de notificação: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
  };

  // Não mostrar se já tem permissão ou se foi fechado
  if (!isVisible || permission === 'granted') {
    return null;
  }

  // Não mostrar se não suporta notificações
  if (permission === null) {
    return null;
  }

  return (
    <PermissionBanner>
      <PermissionContent>
        <PermissionText>
          <strong>
            <span role="img" aria-label="Sino de notificação">🔔</span> Ative as notificações
          </strong>
          <br />
          Receba alertas em tempo real sobre suas conversas e pedidos
        </PermissionText>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <PermissionButton
            onClick={handleRequestPermission}
            disabled={isLoading || permission === 'denied'}
          >
            {isLoading ? 'Carregando...' : permission === 'denied' ? 'Negado' : 'Ativar'}
          </PermissionButton>
          <CloseButton onClick={handleClose}>
            <span role="img" aria-label="Fechar">✕</span>
          </CloseButton>
        </div>
      </PermissionContent>
    </PermissionBanner>
  );
};

export default NotificationPermission;
